import React, { useState, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import { apiClient } from '../../services/apiClient';
import { toastService } from '../../services/toastService';
import PageHeader from '../common/PageHeader/PageHeader';
import Select from '../common/Select/Select';
import Input from '../common/Input/Input';
import Button from '../common/Button/Button';
import ToggleSwitch from '../common/ToggleSwitch/ToggleSwitch';
import './LLMConfiguration.css';

export default function LLMConfiguration() {
  const { state, dispatch, actions } = useAppContext();
  
  // Local state for form data
  const [mainTab, setMainTab] = useState('supervisor'); // 'supervisor', 'agents', 'auto-mode', or 'on-premise'
  const [agentTab, setAgentTab] = useState('openai'); // For agents section
  const [apiKeys, setApiKeys] = useState({
    openai: '',
    anthropic: '',
    google: ''
  });
  const [models, setModels] = useState({
    openai: 'gpt-4o-mini',
    anthropic: 'claude-3-5-haiku-20241022',
    google: 'gemini-pro'
  });
  const [testStatus, setTestStatus] = useState({});
  const [supervisorLLM, setSupervisorLLM] = useState({
    provider: 'openai',
    model: 'gpt-4o-mini'
  });
  const [summaryConfig, setSummaryConfig] = useState({
    provider: 'ollama',
    model: 'gemma3:1b'
  });
  
  // On-premise models configuration
  // When enabled, models are automatically available for both Supervisor and Agents
  const [onPremiseModels, setOnPremiseModels] = useState({
    ollama: {
      baseUrl: import.meta.env.VITE_OLLAMA_BASE_URL || 'http://localhost:11434',
      model: 'gemma:2b',
      enabled: false
    }
  });
  const [onPremiseTestStatus, setOnPremiseTestStatus] = useState({});
  const [availableOllamaModels, setAvailableOllamaModels] = useState([]);
  const [enabledAgents, setEnabledAgents] = useState({
    openai: false,
    anthropic: false,
    google: false
  });
  const [allowUserModelSelection, setAllowUserModelSelection] = useState(state.allowUserModelSelection || false);
  
  // Auto Mode configuration
  const [autoModeEnabled, setAutoModeEnabled] = useState(false);
  const [autoModeManagedBySupervisor, setAutoModeManagedBySupervisor] = useState(false);
  const [autoModeRules, setAutoModeRules] = useState([
    {
      id: 'code-analysis',
      name: 'Code Analysis',
      description: 'Queries involving programming, code review, debugging',
      keywords: ['code', 'programming', 'debug', 'function', 'class', 'variable', 'algorithm', 'syntax', 'error', 'bug'],
      provider: 'anthropic',
      model: 'claude-3-5-haiku-20241022',
      enabled: true
    },
    {
      id: 'research-analysis',
      name: 'Research & Analysis',
      description: 'Queries involving research, analysis, data interpretation',
      keywords: ['research', 'analysis', 'data', 'study', 'report', 'statistics', 'trends', 'market', 'survey'],
      provider: 'openai',
      model: 'gpt-4o-mini',
      enabled: true
    },
    {
      id: 'creative-writing',
      name: 'Creative Writing',
      description: 'Queries involving creative content, writing, storytelling',
      keywords: ['write', 'story', 'creative', 'content', 'blog', 'article', 'poem', 'script', 'narrative'],
      provider: 'openai',
      model: 'gpt-4o-mini',
      enabled: true
    },
    {
      id: 'technical-support',
      name: 'Technical Support',
      description: 'Queries involving technical help, troubleshooting, documentation',
      keywords: ['help', 'support', 'troubleshoot', 'how to', 'guide', 'tutorial', 'documentation', 'setup', 'install'],
      provider: 'anthropic',
      model: 'claude-3-5-haiku-20241022',
      enabled: true
    },
    {
      id: 'general-qa',
      name: 'General Q&A',
      description: 'General questions and answers, fallback for unmatched queries',
      keywords: ['what', 'why', 'how', 'when', 'where', 'explain', 'define', 'describe'],
      provider: 'openai',
      model: 'gpt-4o-mini',
      enabled: true
    }
  ]);

  // Notification system - uses both toastService and status messages for better visibility
  const showNotification = (message, type = 'info') => {
    // Use toastService for better visibility
    if (type === 'success') {
      toastService.success(message, 4000);
    } else if (type === 'error') {
      toastService.error(message, 5000);
    } else if (type === 'warning') {
      toastService.warning(message, 4000);
    } else {
      toastService.info(message, 3000);
    }
    
    // Also add to status messages for consistency
    dispatch({ 
      type: actions.ADD_STATUS_MESSAGE, 
      payload: { message, type, timestamp: Date.now() }
    });
    
    // Auto-clear notification after 5 seconds
    setTimeout(() => {
      dispatch({ type: actions.CLEAR_STATUS_MESSAGES });
    }, 5000);
  };

  // Load saved configuration on component mount
  useEffect(() => {
    const loadConfiguration = async () => {
      try {
        const config = await apiClient.getLLMConfig();
        if (!config) {
          console.log('No LLM configuration found in database');
          return;
        }
        if (config.apiKeys) setApiKeys(config.apiKeys);
        if (config.models) setModels(config.models);
        if (config.supervisorLLM) setSupervisorLLM(config.supervisorLLM);
        if (config.summaryConfig) {
          setSummaryConfig(config.summaryConfig);
          dispatch({ type: actions.SET_SUMMARY_CONFIG, payload: config.summaryConfig });
        } else {
          const defaultSummary = { provider: 'ollama', model: 'gemma3:1b' };
          setSummaryConfig(defaultSummary);
          dispatch({ type: actions.SET_SUMMARY_CONFIG, payload: defaultSummary });
        }
        if (config.enabledAgents) {
          setEnabledAgents(config.enabledAgents);
          dispatch({ type: actions.SET_ENABLED_AGENTS, payload: config.enabledAgents });
        }
        if (config.allowUserModelSelection !== undefined) {
          setAllowUserModelSelection(config.allowUserModelSelection);
          dispatch({ type: actions.SET_ALLOW_USER_MODEL_SELECTION, payload: config.allowUserModelSelection });
        }
        if (config.autoModeEnabled !== undefined) {
          setAutoModeEnabled(config.autoModeEnabled);
          dispatch({ type: actions.SET_AUTO_MODE_ENABLED, payload: config.autoModeEnabled });
        }
        if (config.autoModeManagedBySupervisor !== undefined) {
          setAutoModeManagedBySupervisor(config.autoModeManagedBySupervisor);
          dispatch({ type: actions.SET_AUTO_MODE_MANAGED_BY_SUPERVISOR, payload: config.autoModeManagedBySupervisor });
        }
        if (config.autoModeRules) setAutoModeRules(config.autoModeRules);
        if (config.onPremiseModels) {
          setOnPremiseModels(config.onPremiseModels);
          dispatch({ type: actions.SET_ON_PREMISE_MODELS, payload: config.onPremiseModels });
        }
        
        console.log('📥 Loaded LLM config from database:', {
          autoModeEnabled: config.autoModeEnabled,
          autoModeManagedBySupervisor: config.autoModeManagedBySupervisor,
          allowUserModelSelection: config.allowUserModelSelection,
          onPremiseModels: config.onPremiseModels
        });
      } catch (error) {
        console.error('Error loading LLM configuration from database:', error);
      }
    };
    
    // Load configuration from database
    loadConfiguration();
  }, [actions.SET_SUMMARY_CONFIG, dispatch]);
  
  // Sync local state with context state
  useEffect(() => {
    console.log('Syncing Auto Mode state with context:', {
      local: { autoModeEnabled, autoModeManagedBySupervisor },
      context: {
        autoModeEnabled: state.autoModeEnabled,
        autoModeManagedBySupervisor: state.autoModeManagedBySupervisor
      }
    });
  }, [autoModeEnabled, autoModeManagedBySupervisor, state.autoModeEnabled, state.autoModeManagedBySupervisor]);

  const handleApiKeyChange = (provider, value) => {
    setApiKeys(prev => ({
      ...prev,
      [provider]: value
    }));
  };

  const handleModelChange = (provider, value) => {
    setModels(prev => ({
      ...prev,
      [provider]: value
    }));
  };

  const testApiKey = async (provider) => {
    if (!apiKeys[provider]) {
      setTestStatus(prev => ({ ...prev, [provider]: 'error' }));
      showNotification('Please enter an API key first', 'error');
      return;
    }
    
    setTestStatus(prev => ({ ...prev, [provider]: 'testing' }));
    showNotification(`Testing ${provider.toUpperCase()} API key...`, 'info');
    
    try {
      const response = await apiClient.testApiKey(provider, apiKeys[provider]);
      
      if (response.status === 'success') {
        setTestStatus(prev => ({ ...prev, [provider]: 'success' }));
        showNotification(`${provider.toUpperCase()} API key is valid!`, 'success');
      } else {
        setTestStatus(prev => ({ ...prev, [provider]: 'error' }));
        const errorMessage = response.message || 'Invalid API key or API error';
        showNotification(`${provider.toUpperCase()}: ${errorMessage}`, 'error');
      }
    } catch (error) {
      console.error(`Error testing ${provider} API key:`, error);
      setTestStatus(prev => ({ ...prev, [provider]: 'error' }));
      
      // Extract more specific error message
      let errorMessage = 'Connection error or invalid API key';
      if (error.message) {
        if (error.message.includes('401')) {
          errorMessage = 'Invalid API key - please check your key';
        } else if (error.message.includes('403')) {
          errorMessage = 'API key lacks required permissions';
        } else if (error.message.includes('429')) {
          errorMessage = 'Rate limit exceeded - try again later';
        } else if (error.message.includes('500')) {
          errorMessage = 'Server error - try again later';
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          errorMessage = 'Network connection error';
        } else {
          errorMessage = error.message;
        }
      }
      
      showNotification(`${provider.toUpperCase()}: ${errorMessage}`, 'error');
    }
  };

  // Helper to save complete configuration to database
  const saveToDatabase = async () => {
    try {
      const config = {
        apiKeys,
        models,
        supervisorLLM,
        summaryConfig,
        enabledAgents,
        allowUserModelSelection,
        autoModeEnabled,
        autoModeManagedBySupervisor,
        autoModeRules,
        onPremiseModels
      };
      
      await apiClient.saveLLMConfig(config);
      console.log('📦 Configuration saved to database');
    } catch (error) {
      console.error('❌ Error saving configuration to database:', error);
      throw error;
    }
  };


  const saveSupervisorConfiguration = async () => {
    // Get current config or create new one
    const savedConfig = localStorage.getItem('llm-config');
    let config = {};
    
    if (savedConfig) {
      try {
        config = JSON.parse(savedConfig);
      } catch (error) {
        console.error('Error parsing saved config:', error);
      }
    }
    
    // Update supervisor configuration
    config.supervisorLLM = supervisorLLM;
    config.summaryConfig = summaryConfig;
    config.timestamp = new Date().toISOString();
    
    localStorage.setItem('llm-config', JSON.stringify(config));
    await saveToDatabase();
    
    // Save supervisor LLM configuration to context
    dispatch({ type: actions.SET_SUPERVISOR_LLM, payload: supervisorLLM });
    dispatch({ type: actions.SET_SUMMARY_CONFIG, payload: summaryConfig });
    
    console.log('Supervisor LLM configuration saved:', supervisorLLM);
    
    // Show success notification with clear message
    showNotification('Supervisor configuration saved successfully!', 'success');
  };

  const saveAgentsConfiguration = async () => {
    // Get current config or create new one
    const savedConfig = localStorage.getItem('llm-config');
    let config = {};
    
    if (savedConfig) {
      try {
        config = JSON.parse(savedConfig);
      } catch (error) {
        console.error('Error parsing saved config:', error);
      }
    }
    
    // Auto-enable agents that have API keys configured
    const updatedEnabledAgents = { ...enabledAgents };
    Object.keys(apiKeys).forEach(provider => {
      if (apiKeys[provider] && apiKeys[provider].trim() !== '') {
        // If API key is configured, automatically enable the agent
        updatedEnabledAgents[provider] = true;
      }
    });
    
    // Update local state to reflect auto-enabled agents
    setEnabledAgents(updatedEnabledAgents);
    
    // Update agents configuration
    config.apiKeys = apiKeys;
    config.models = models;
    config.enabledAgents = updatedEnabledAgents;
    config.allowUserModelSelection = allowUserModelSelection;
    config.timestamp = new Date().toISOString();
    
    localStorage.setItem('llm-config', JSON.stringify(config));
    await saveToDatabase();
    
    // Save enabled agents configuration to context
    dispatch({ type: actions.SET_ENABLED_AGENTS, payload: updatedEnabledAgents });
    dispatch({ type: actions.SET_ALLOW_USER_MODEL_SELECTION, payload: allowUserModelSelection });
    // Save per-provider model preferences to context
    dispatch({ type: actions.SET_AGENT_MODELS, payload: models });
    
    console.log('Agents configuration saved:', { 
      apiKeys: Object.keys(apiKeys), 
      models, 
      enabledAgents: updatedEnabledAgents,
      allowUserModelSelection 
    });
    
    // Count saved API keys
    const savedApiKeysCount = Object.values(apiKeys).filter(key => key && key.trim() !== '').length;
    const enabledAgentsCount = Object.values(updatedEnabledAgents).filter(enabled => enabled).length;
    
    // Show success notification with details
    const message = savedApiKeysCount > 0 
      ? `Agents configuration saved successfully! ${savedApiKeysCount} API key(s) and ${enabledAgentsCount} agent(s) configured.`
      : `Agents configuration saved successfully! ${enabledAgentsCount} agent(s) configured.`;
    showNotification(message, 'success');
  };

  const saveAutoModeConfiguration = async () => {
    // Get current config or create new one
    const savedConfig = localStorage.getItem('llm-config');
    let config = {};
    
    if (savedConfig) {
      try {
        config = JSON.parse(savedConfig);
      } catch (error) {
        console.error('Error parsing saved config:', error);
      }
    }
    
    // Update auto mode configuration
    config.autoModeEnabled = autoModeEnabled;
    config.autoModeManagedBySupervisor = autoModeManagedBySupervisor;
    config.autoModeRules = autoModeRules;
    config.timestamp = new Date().toISOString();
    
    localStorage.setItem('llm-config', JSON.stringify(config));
    await saveToDatabase();
    
    // Save auto mode configuration to context
    dispatch({ type: actions.SET_AUTO_MODE_ENABLED, payload: autoModeEnabled });
    dispatch({ type: actions.SET_AUTO_MODE_MANAGED_BY_SUPERVISOR, payload: autoModeManagedBySupervisor });
    dispatch({ type: actions.SET_AUTO_MODE_RULES, payload: autoModeRules });
    
    console.log('🔧 Auto Mode configuration saved:', { 
      autoModeEnabled, 
      autoModeManagedBySupervisor,
      autoModeRules 
    });
    console.log('📤 Dispatched actions:', {
      SET_AUTO_MODE_ENABLED: autoModeEnabled,
      SET_AUTO_MODE_MANAGED_BY_SUPERVISOR: autoModeManagedBySupervisor,
      SET_AUTO_MODE_RULES: autoModeRules
    });
    
    // Show success notification
    showNotification('Auto Mode configuration saved successfully!', 'success');
  };

  const updateAutoModeRule = (ruleId, updates) => {
    setAutoModeRules(prev => 
      prev.map(rule => {
        if (rule.id !== ruleId) return rule;
        
        const updatedRule = { ...rule, ...updates };
        
        // If provider changed, reset model to first model of the new provider
        if (updates.provider && updates.provider !== rule.provider) {
          const newProvider = cloudProviders.find(p => p.id === updates.provider);
          if (newProvider && newProvider.models && newProvider.models.length > 0) {
            updatedRule.model = newProvider.models[0].value;
          }
        }
        
        return updatedRule;
      })
    );
  };

  const addAutoModeRule = () => {
    const defaultProvider = cloudProviders[0];
    const newRule = {
      id: `rule-${Date.now()}`,
      name: 'New Rule',
      description: 'Custom rule for query routing',
      keywords: ['keyword1', 'keyword2'],
      provider: defaultProvider?.id || 'openai',
      model: defaultProvider?.models?.[0]?.value || 'gpt-4o',
      enabled: true
    };
    setAutoModeRules(prev => [...prev, newRule]);
  };

  const removeAutoModeRule = (ruleId) => {
    setAutoModeRules(prev => prev.filter(rule => rule.id !== ruleId));
  };

  // On-premise model functions
  const testOnPremiseModel = async (provider) => {
    const config = onPremiseModels[provider];
    if (!config.baseUrl) {
      showNotification(`Please enter a base URL for ${provider}`, 'error');
      return;
    }
    
    setOnPremiseTestStatus(prev => ({ ...prev, [provider]: 'testing' }));
    showNotification(`Testing ${provider} connection...`, 'info');
    
    try {
      // Test connection to on-premise model
      let testUrl = '';
      if (provider === 'ollama') {
        testUrl = `${config.baseUrl}/api/tags`;
      } else {
        testUrl = `${config.baseUrl}/v1/models`;
      }
      
      const response = await fetch(testUrl, {
        method: 'GET',
        headers: config.apiKey ? { 'Authorization': `Bearer ${config.apiKey}` } : {}
      });
      
      if (response.ok) {
        setOnPremiseTestStatus(prev => ({ ...prev, [provider]: 'success' }));
        showNotification(`${provider.toUpperCase()} connection successful!`, 'success');
        
        // Load available models if Ollama
        if (provider === 'ollama') {
          const data = await response.json();
          if (data.models && Array.isArray(data.models)) {
            setAvailableOllamaModels(data.models.map(m => m.name));
          }
        }
      } else {
        setOnPremiseTestStatus(prev => ({ ...prev, [provider]: 'error' }));
        showNotification(`${provider.toUpperCase()} connection failed: ${response.statusText}`, 'error');
      }
    } catch (error) {
      console.error(`Error testing ${provider}:`, error);
      setOnPremiseTestStatus(prev => ({ ...prev, [provider]: 'error' }));
      showNotification(`${provider.toUpperCase()} connection error. Make sure the server is running.`, 'error');
    }
  };

  const updateOnPremiseModel = (provider, updates) => {
    setOnPremiseModels(prev => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        ...updates
      }
    }));
  };

  const saveOnPremiseConfiguration = async () => {
    const savedConfig = localStorage.getItem('llm-config');
    let config = {};
    
    if (savedConfig) {
      try {
        config = JSON.parse(savedConfig);
      } catch (error) {
        console.error('Error parsing saved config:', error);
      }
    }
    
    // Update on-premise configuration
    config.onPremiseModels = onPremiseModels;
    config.timestamp = new Date().toISOString();
    
    localStorage.setItem('llm-config', JSON.stringify(config));
    await saveToDatabase();
    
    // Save to context if needed
    dispatch({ type: actions.SET_ON_PREMISE_MODELS, payload: onPremiseModels });
    
    console.log('On-premise models configuration saved:', onPremiseModels);
    
    // Show success notification
    showNotification('On-premise models configuration saved successfully!', 'success');
  };

  const providerIcons = {
    openai: 'fas fa-robot',
    anthropic: 'fas fa-brain',
    google: 'fab fa-google'
  };
  const cloudProviders = Object.entries(state.systemProviders || {}).map(([providerId, provider]) => ({
    id: providerId,
    name: provider?.name || providerId,
    icon: providerIcons[providerId] || 'fas fa-microchip',
    models: (provider?.models || []).map((model) => ({ value: model, label: model }))
  }));

  // On-premise providers for supervisor selection
  // All enabled on-premise models are automatically available for Supervisor and Agents
  const onPremiseProviders = [];
  
  if (onPremiseModels.ollama.enabled) {
    onPremiseProviders.push({
      id: 'ollama',
      name: 'Ollama (On-Premise)',
      icon: 'fas fa-server',
      models: availableOllamaModels.length > 0 ? availableOllamaModels.map(model => ({
        value: model,
        label: model
      })) : [
        { value: onPremiseModels.ollama.model, label: onPremiseModels.ollama.model }
      ]
    });
  }
  

  // Combined providers for supervisor selection
  const allProviders = [...cloudProviders, ...onPremiseProviders];

  // Providers for summarization: always include Ollama (default) plus cloud providers
  const defaultOllamaModels = [
    { value: 'gemma3:1b', label: 'gemma3:1b' },
    { value: 'gemma:2b', label: 'gemma:2b' },
    { value: 'llama2', label: 'llama2' },
    { value: 'mistral', label: 'mistral' }
  ];
  const summaryProviders = [
    ...cloudProviders,
    {
      id: 'ollama',
      name: 'Ollama (On-Premise)',
      icon: 'fas fa-server',
      models: availableOllamaModels.length > 0
        ? availableOllamaModels.map((m) => ({ value: m, label: m }))
        : defaultOllamaModels
    }
  ];

  return (
    <div className="page-container llm-configuration">
      <div className="page-content">
        <PageHeader
          title="LLM Configuration"
          description="Configure LLM models for the supervisor and specialized agents. Set up API keys, models, and automatic query routing."
          icon="fas fa-robot"
          accentColor="green"
        />

        <div className="llm-config-content">
        {/* Main Section Tabs */}
        <div className="main-tabs">
          <button
            className={`main-tab ${mainTab === 'supervisor' ? 'active' : ''}`}
            onClick={() => setMainTab('supervisor')}
          >
            <i className="fas fa-crown"></i>
            Supervisor Configuration
          </button>
          <button
            className={`main-tab ${mainTab === 'agents' ? 'active' : ''}`}
            onClick={() => setMainTab('agents')}
          >
            <i className="fas fa-users"></i>
            Agents Configuration
          </button>
          <button
            className={`main-tab ${mainTab === 'auto-mode' ? 'active' : ''}`}
            onClick={() => setMainTab('auto-mode')}
          >
            <i className="fas fa-magic"></i>
            Auto Mode
          </button>
          <button
            className={`main-tab ${mainTab === 'on-premise' ? 'active' : ''}`}
            onClick={() => setMainTab('on-premise')}
          >
            <i className="fas fa-server"></i>
            On-Premise Models
          </button>
          <button
            className={`main-tab ${mainTab === 'summarization' ? 'active' : ''}`}
            onClick={() => setMainTab('summarization')}
          >
            <i className="fas fa-align-left"></i>
            Summarization Model
          </button>
        </div>

        {/* Supervisor Configuration Section */}
        {mainTab === 'supervisor' && (
          <div className="supervisor-section">
            <div className="section-header">
              <h3>Supervisor Agent Configuration</h3>
              <p className="section-description">
                <i className="fas fa-info-circle"></i>
                The supervisor coordinates query processing and uses a model as a tool to analyze and respond based exclusively on stored memory/context. 
              </p>
            </div>
            
            <div className="supervisor-config">
              <Select
                label="LLM Provider"
                value={supervisorLLM.provider}
                onChange={(e) => {
                  const provider = e.target.value;
                  const defaultModel = allProviders.find(p => p.id === provider)?.models[0]?.value || 'gpt-4o';
                  setSupervisorLLM({ provider, model: defaultModel });
                }}
                options={allProviders.map(p => ({ value: p.id, label: p.name }))}
              />
              
              <Select
                label="Model"
                value={supervisorLLM.model}
                onChange={(e) => setSupervisorLLM(prev => ({ ...prev, model: e.target.value }))}
                options={allProviders.find(p => p.id === supervisorLLM.provider)?.models || []}
              />

              <div className="supervisor-info">
                <div className="info-box">
                  <i className="fas fa-lightbulb"></i>
                  <div>
                    <strong>Note:</strong> {supervisorLLM.provider === 'ollama' || supervisorLLM.provider === 'local' 
                      ? `This is an on-premise model. Make sure it's configured in the On-Premise Models section.`
                      : `The supervisor uses the API key configured in the Agents section for the selected provider. Make sure to configure the API key for ${allProviders.find(p => p.id === supervisorLLM.provider)?.name} in the Agents section.`}
                  </div>
                </div>
                {supervisorLLM.model === 'gpt-5' && (
                  <div className="info-box" style={{borderLeftColor: '#f59e0b', marginTop: '1rem'}}>
                    <i className="fas fa-exclamation-triangle" style={{color: '#f59e0b'}}></i>
                    <div>
                      <strong>GPT-5 Preview:</strong> GPT-5 is not yet available in OpenAI's API. 
                      This option is prepared for future availability. Use GPT-4 or GPT-4 Turbo for current functionality.
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="section-actions">
              <Button variant="primary" icon="fas fa-save" onClick={saveSupervisorConfiguration}>
                Save Supervisor Configuration
              </Button>
            </div>
          </div>
        )}

        {/* Summarization Model Section - same structure as Supervisor for consistent UI */}
        {mainTab === 'summarization' && (
          <div className="supervisor-section">
            <div className="section-header">
              <h3>Summarization Model</h3>
              <p className="section-description">
                <i className="fas fa-info-circle"></i>
                This model summarizes multi-provider responses into a single answer.
              </p>
            </div>

            <div className="supervisor-config">
              <Select
                label="Summary Provider"
                value={summaryConfig.provider}
                onChange={(e) => {
                  const provider = e.target.value;
                  const defaultModel = summaryProviders.find(p => p.id === provider)?.models[0]?.value || 'gemma3:1b';
                  setSummaryConfig({ provider, model: defaultModel });
                }}
                options={summaryProviders.map(p => ({ value: p.id, label: p.name }))}
              />

              <Select
                label="Summary Model"
                value={summaryConfig.model}
                onChange={(e) => setSummaryConfig(prev => ({ ...prev, model: e.target.value }))}
                options={(() => {
                  const list = summaryProviders.find(p => p.id === summaryConfig.provider)?.models || [];
                  const hasCurrent = list.some((o) => o.value === summaryConfig.model);
                  return hasCurrent ? list : [{ value: summaryConfig.model, label: summaryConfig.model }, ...list];
                })()}
              />

              <div className="supervisor-info">
                <div className="info-box">
                  <i className="fas fa-lightbulb"></i>
                  <div>
                    <strong>Note:</strong> {summaryConfig.provider === 'ollama'
                      ? 'Ollama is typically run on-premise. Configure it in the On-Premise Models tab if needed.'
                      : `The summary model uses the API key configured in the Agents section for ${summaryProviders.find(p => p.id === summaryConfig.provider)?.name}.`}
                  </div>
                </div>
              </div>
            </div>

            <div className="section-actions">
              <Button variant="primary" icon="fas fa-save" onClick={saveSupervisorConfiguration}>
                Save Summarization Configuration
              </Button>
            </div>
          </div>
        )}

        {/* Agents Configuration Section */}
        {mainTab === 'agents' && (
          <div className="agents-section">
            <div className="agent-tabs-sidebar">
              {cloudProviders.map((provider) => (
                <button
                  key={provider.id}
                  className={`agent-sidebar-tab ${agentTab === provider.id ? 'active' : ''}`}
                  onClick={() => setAgentTab(provider.id)}
                >
                  {provider.name}
                </button>
              ))}
            </div>

            <div className="agent-config-detail">
              {cloudProviders.map((provider) => (
                <div key={provider.id} style={{ display: agentTab === provider.id ? 'block' : 'none' }}>
                  <div className="agent-header-row">
                    <h3>{provider.name} Settings</h3>
                    <ToggleSwitch
                      label={enabledAgents[provider.id] ? 'Agent Enabled' : 'Agent Disabled'}
                      checked={enabledAgents[provider.id]}
                      onChange={(val) => setEnabledAgents((prev) => ({ ...prev, [provider.id]: val }))}
                    />
                  </div>

                  <div className="form-group" style={{ marginTop: '1.5rem' }}>
                    <label>API Key</label>
                    <div className="input-with-button">
                      <Input
                        type="password"
                        value={apiKeys[provider.id]}
                        onChange={(e) => handleApiKeyChange(provider.id, e.target.value)}
                        placeholder="sk-..."
                      />
                      <Button
                        variant="secondary"
                        onClick={() => testApiKey(provider.id)}
                        disabled={!apiKeys[provider.id] || testStatus[provider.id] === 'testing'}
                      >
                        {testStatus[provider.id] === 'testing' ? 'Testing...' : 'Test'}
                      </Button>
                    </div>
                    {testStatus[provider.id] === 'success' && (
                      <p className="status-message success">
                        API key is valid and working!
                      </p>
                    )}
                    {testStatus[provider.id] === 'error' && (
                      <p className="status-message error">
                        Invalid API key or connection error.
                      </p>
                    )}
                  </div>

                  <Select
                    label="Default Model"
                    value={models[provider.id]}
                    onChange={(e) => handleModelChange(provider.id, e.target.value)}
                    options={provider.models}
                  />

                  <div className="provider-info">
                    {provider.id === 'openai' && (
                      <p>
                        Get your API key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">OpenAI Platform</a>
                      </p>
                    )}
                    {provider.id === 'anthropic' && (
                      <p>
                        Get your API key from <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer">Anthropic Console</a>
                      </p>
                    )}
                    {provider.id === 'google' && (
                      <p>
                        Get your API key from <a href="https://ai.google.dev/" target="_blank" rel="noopener noreferrer">Google AI Studio</a>
                      </p>
                    )}
                  </div>
                </div>
              ))}

              <div className="setting-card" style={{ marginTop: '1.5rem' }}>
                <div className="setting-row">
                  <div className="setting-info">
                    <span className="setting-label">Allow User Model Selection</span>
                    <p className="setting-desc">Let users choose model versions in the chat interface.</p>
                  </div>
                  <ToggleSwitch
                    checked={allowUserModelSelection}
                    onChange={setAllowUserModelSelection}
                  />
                </div>
              </div>

              <div className="section-actions">
                <Button variant="primary" icon="fas fa-save" onClick={saveAgentsConfiguration}>
                  Save Agents Configuration
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Auto Mode Configuration Section */}
        {mainTab === 'auto-mode' && (
          <div className="auto-mode-section">
            <div className="settings-list">
              <div className="setting-card">
                <div className="setting-row">
                  <div className="setting-info">
                    <span className="setting-label">Enable Auto Mode</span>
                    <p className="setting-desc">Automatically route queries to specific agents based on keywords.</p>
                  </div>
                  <ToggleSwitch
                    checked={autoModeEnabled}
                    onChange={setAutoModeEnabled}
                  />
                </div>
              </div>

              {autoModeEnabled && (
                <div className="setting-card">
                  <div className="setting-row">
                    <div className="setting-info">
                      <span className="setting-label">Use Supervisor AI</span>
                      <p className="setting-desc">Use an LLM to intelligently analyze intent instead of simple keywords.</p>
                    </div>
                    <ToggleSwitch
                      checked={autoModeManagedBySupervisor}
                      onChange={setAutoModeManagedBySupervisor}
                    />
                  </div>
                </div>
              )}
            </div>

            {autoModeEnabled && !autoModeManagedBySupervisor && (
              <div className="rules-section">
                <div className="rules-header">
                  <h4>Routing Rules</h4>
                  <Button variant="secondary" size="sm" icon="fas fa-plus" onClick={addAutoModeRule}>
                    Add Rule
                  </Button>
                </div>

                <div className="rules-list">
                  {autoModeRules.map((rule) => (
                    <div key={rule.id} className="rule-card">
                      <div className="rule-header">
                        <div className="rule-info">
                          <h5>{rule.name}</h5>
                          <p>{rule.description}</p>
                        </div>
                        <div className="rule-controls">
                          <ToggleSwitch
                            size="sm"
                            checked={rule.enabled}
                            onChange={(val) => updateAutoModeRule(rule.id, { enabled: val })}
                          />
                          <Button
                            variant="danger"
                            size="sm"
                            icon="fas fa-trash"
                            onClick={() => removeAutoModeRule(rule.id)}
                            title="Remove rule"
                          />
                        </div>
                      </div>

                      <div className="rule-content">
                        <div className="rule-field">
                          <Input
                            label="Name"
                            value={rule.name}
                            onChange={(e) => updateAutoModeRule(rule.id, { name: e.target.value })}
                          />
                        </div>

                        <div className="rule-field">
                          <Input
                            label="Description"
                            value={rule.description}
                            onChange={(e) => updateAutoModeRule(rule.id, { description: e.target.value })}
                          />
                        </div>

                        <div className="rule-field">
                          <Input
                            label="Keywords (comma-separated)"
                            value={rule.keywords.join(', ')}
                            onChange={(e) => updateAutoModeRule(rule.id, { 
                              keywords: e.target.value.split(',').map(k => k.trim()).filter(k => k)
                            })}
                            placeholder="code, programming, debug, function..."
                          />
                        </div>

                        <div className="rule-field-group">
                          <div className="rule-field">
                            <Select
                              label="Provider"
                              value={rule.provider}
                              onChange={(e) => updateAutoModeRule(rule.id, { provider: e.target.value })}
                              options={cloudProviders.map(p => ({ value: p.id, label: p.name }))}
                            />
                          </div>

                          <div className="rule-field">
                            <Select
                              label="Model"
                              value={rule.model}
                              onChange={(e) => updateAutoModeRule(rule.id, { model: e.target.value })}
                              options={cloudProviders.find(p => p.id === rule.provider)?.models || []}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="section-actions">
              <Button variant="primary" icon="fas fa-save" onClick={saveAutoModeConfiguration}>
                Save Auto Mode Configuration
              </Button>
            </div>
          </div>
        )}

        {/* On-Premise Models Configuration Section */}
        {mainTab === 'on-premise' && (
          <div className="on-premise-section">
            <div className="section-header">
              <h3><i className="fas fa-server"></i> On-Premise Models</h3>
            </div>

            <div className="setting-card">
              <div className="setting-row">
                <div className="setting-info">
                  <span className="setting-label">Ollama Integration</span>
                  <p className="setting-desc">Run models locally on your infrastructure — cost-free with complete privacy.</p>
                </div>
                <ToggleSwitch
                  label={onPremiseModels.ollama.enabled ? 'Enabled' : 'Disabled'}
                  checked={onPremiseModels.ollama.enabled}
                  onChange={(val) => updateOnPremiseModel('ollama', { enabled: val })}
                />
              </div>

              {onPremiseModels.ollama.enabled && (
                <div className="on-premise-grid">
                  {/* Base URL Input */}
                  <div className="form-group">
                    <label>Base URL</label>
                    <div className="input-with-button">
                      <Input
                        type="text"
                        value={onPremiseModels.ollama.baseUrl}
                        onChange={(e) => updateOnPremiseModel('ollama', { baseUrl: e.target.value })}
                        placeholder="http://localhost:11434"
                      />
                      <Button
                        variant="secondary"
                        onClick={() => testOnPremiseModel('ollama')}
                        disabled={!onPremiseModels.ollama.baseUrl || onPremiseTestStatus.ollama === 'testing'}
                      >
                        {onPremiseTestStatus.ollama === 'testing' ? 'Testing...' : 'Test'}
                      </Button>
                    </div>
                    {onPremiseTestStatus.ollama === 'success' && (
                      <p className="status-message success">
                        Ollama is running and accessible!
                      </p>
                    )}
                    {onPremiseTestStatus.ollama === 'error' && (
                      <p className="status-message error">
                        Cannot connect to Ollama. Make sure it's running.
                      </p>
                    )}
                  </div>

                  {/* Model Selection */}
                  <div className="form-group">
                    <label>Model</label>
                    <div className="model-input-group">
                      {availableOllamaModels.length > 0 ? (
                        <Select
                          value={onPremiseModels.ollama.model}
                          onChange={(e) => updateOnPremiseModel('ollama', { model: e.target.value })}
                          options={availableOllamaModels.map(m => ({ value: m, label: m }))}
                        />
                      ) : (
                        <Input
                          type="text"
                          value={onPremiseModels.ollama.model}
                          onChange={(e) => updateOnPremiseModel('ollama', { model: e.target.value })}
                          placeholder="gemma:2b"
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="section-actions">
              <Button variant="primary" icon="fas fa-save" onClick={saveOnPremiseConfiguration}>
                Save On-Premise Configuration
              </Button>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
